import { useEffect, useMemo, useState } from 'react';

import { ChakraBox, ChakraWrap, ChakraWrapItem } from '@equip.health/ui';

import { GOAL_STATUS, WeightGoalTypes } from '~/lib/constants/goals.constants';
import { PatientGender } from '~/lib/constants/patients.constants';
import {
  computeBMI,
  computeTargetWeight,
  formatHeight,
  getAgeForMBmi,
  getMedianGoalWeight,
  round,
} from '~/lib/util/clinicalProfile.util';
import {
  useGetMedianBMIQuery,
  useGetPatientClinicalMeasurementsQuery,
  useGetPatientWeightGoalsQuery,
} from '~/store/services/clinicalData';

import ClinicalMeasurementCard, {
  ObservationContent,
} from './ClinicalMeasurementCard';
import {
  ObservationCards,
  ObservationStatusType,
  PatientVitalsTabs,
  PatientVitalsTabsCollection,
} from './constants';
import SinglePatientReportCard from './SinglePatientReportCard';

const TOP_LEVEL_ID = 'clinical-measurements';

export type MeasurementCardDefinition = {
  dataToGraph?: PatientVitalsObservation[];
  id: string;
  mainObservation: ObservationContent;
  profileType: ObservationCards;
  secondaryText?: string;
  title: string;
  tooltipContent?: string;
};

const VALID_STATUSES: ObservationStatusType[] = [
  ObservationStatusType.CORRECTED,
  ObservationStatusType.FINAL,
];

const getObservationsOptions = (type: PatientVitalsTabs) => ({
  'observation-status': VALID_STATUSES,
  'observation-type': PatientVitalsTabsCollection[type].filterBy,
});

const buildBmiCardData = ({
  bmi,
  datetime,
  percentMBmi,
  secondaryText,
}: {
  bmi: number;
  datetime: string;
  percentMBmi: number;
  secondaryText?: string;
}): MeasurementCardDefinition => ({
  id: `${TOP_LEVEL_ID}__bmi`,
  mainObservation: {
    observationDatetime: datetime,
    unit: percentMBmi ? '%' : '',
    value: percentMBmi || bmi,
  },
  profileType: ObservationCards.BMI,
  secondaryText,
  title: percentMBmi ? '%mBMI' : 'Absolute BMI',
});

const buildTargetWeightCardData = (
  value: number,
  datetime: string,
  tooltipContent: string,
): MeasurementCardDefinition => {
  return {
    id: `${TOP_LEVEL_ID}__target-weight`,
    mainObservation: {
      observationDatetime: datetime,
      unit: '%',
      value,
    },
    profileType: ObservationCards.TARGET_WEIGHT,
    title: '% Target weight',
    tooltipContent,
  };
};

type ClinicalMeasurementOverviewProps = {
  onClickCard?: (profileType: ObservationCards) => void;
  patient: Patient;
  setBmiCardData: (data: MeasurementCardDefinition) => void;
  setTargetWeightCardData: (data: MeasurementCardDefinition) => void;
};

const ClinicalMeasurementOverview = ({
  onClickCard,
  patient,
  setBmiCardData,
  setTargetWeightCardData,
}: ClinicalMeasurementOverviewProps) => {
  const [bmiDatetime, setBmiDatetime] = useState<string>('');
  const [rawBmi, setRawBmi] = useState<number>(0);
  const [percentMBmi, setPercentMBmi] = useState<number>(0);
  const [goalDatetime, setGoalDatetime] = useState<string>('');
  const [percentTW, setPercentTW] = useState<number>(0);

  const { dateOfBirth, sex, userId: patientId } = patient;

  const { isLoading: isBloodPressureDataLoading, data: bloodPressureResponse } =
    useGetPatientClinicalMeasurementsQuery({
      patientExternalId: patientId,
      filterBy: getObservationsOptions(PatientVitalsTabs.BLOOD_PRESSURE),
    });

  const { isLoading: isHeartRateDataLoading, data: heartRateResponse } =
    useGetPatientClinicalMeasurementsQuery({
      patientExternalId: patientId,
      filterBy: getObservationsOptions(PatientVitalsTabs.HEART_RATE),
    });

  const { isLoading: isHeightDataLoading, data: heightResponse } =
    useGetPatientClinicalMeasurementsQuery({
      patientExternalId: patientId,
      filterBy: getObservationsOptions(PatientVitalsTabs.HEIGHT),
    });

  const { isLoading: isWeightDataLoading, data: weightResponse } =
    useGetPatientClinicalMeasurementsQuery({
      patientExternalId: patientId,
      filterBy: getObservationsOptions(PatientVitalsTabs.WEIGHT),
    });

  const { isLoading: isWeightGoalsDataLoading, data: weightGoalsResponse } =
    useGetPatientWeightGoalsQuery({
      patientExternalId: patientId,
      observationType:
        PatientVitalsTabsCollection[PatientVitalsTabs.WEIGHT].filterBy,
    });
  const { data: medianBmiData } = useGetMedianBMIQuery(
    {
      age: getAgeForMBmi(dateOfBirth),
      sex,
    },
    {
      skip: !(
        getAgeForMBmi(dateOfBirth) != null &&
        (sex?.toLowerCase() === PatientGender.MALE.toLowerCase() ||
          sex?.toLowerCase() === PatientGender.FEMALE.toLowerCase())
      ),
    },
  );

  const latestActiveWeightGoal: PatientWeightGoalType = weightGoalsResponse
    ?.filter(({ goalStatus }) => goalStatus === GOAL_STATUS.ACTIVE)
    ?.sort(
      (a, b) =>
        Number(new Date(b.updatedAt || b.createdAt)) -
        Number(new Date(a.updatedAt || a.createdAt)),
    )?.[0];

  const bloodPressureData = bloodPressureResponse?.data ?? [];
  const heartRateData = heartRateResponse?.data ?? [];
  const heightData = heightResponse?.data ?? [];
  const weightData = weightResponse?.data ?? [];

  const isObservationsDataLoading =
    isBloodPressureDataLoading ||
    isHeartRateDataLoading ||
    isHeightDataLoading ||
    isWeightDataLoading ||
    isWeightGoalsDataLoading;

  const targetWeightTooltip = useMemo(() => {
    const medianGoalWeight = getMedianGoalWeight(latestActiveWeightGoal);
    const weightDataValue = weightData[0] && weightData[0].value;
    const denominatorText = latestActiveWeightGoal?.highBound
      ? '(median of weight goal range)'
      : '(low boundary value)';

    if (medianGoalWeight === 'NA' || !weightDataValue) {
      return '';
    }

    return `${weightDataValue} lbs (last weight value) / ${getMedianGoalWeight(
      latestActiveWeightGoal,
    )} ${denominatorText}`;
  }, [weightData[0], weightGoalsResponse, latestActiveWeightGoal]);
  useEffect(() => {
    const { bmi, datetime } = computeBMI(heightData[0], weightData[0]);
    setRawBmi(bmi);
    const { medianBMI } = medianBmiData ?? {};
    if (!bmi) {
      setBmiCardData(null);
      setPercentMBmi(0);
    } else {
      setBmiDatetime(datetime);
      const secondaryTextBase = `${weightData[0].value} lbs / ${formatHeight(
        heightData[0].value,
      )}`;
      if (medianBMI) {
        const pctMBmi = round((bmi / medianBMI) * 100);
        setPercentMBmi(pctMBmi);
        setBmiCardData(
          buildBmiCardData({
            bmi,
            datetime,
            percentMBmi: pctMBmi,
            secondaryText: `${secondaryTextBase} / ${round(bmi)} BMI`,
          }),
        );
      } else {
        setPercentMBmi(0);
        setBmiCardData(
          buildBmiCardData({
            bmi,
            datetime,
            percentMBmi: 0,
            secondaryText: secondaryTextBase,
          }),
        );
      }
    }

    const targetWeight = computeTargetWeight(
      latestActiveWeightGoal,
      weightData[0],
    );
    if (
      weightGoalsResponse &&
      weightData?.[0] &&
      latestActiveWeightGoal?.goalType !== WeightGoalTypes.WEIGHT_MONITORING &&
      targetWeight.tw !== Infinity
    ) {
      const { tw, datetime } = targetWeight;
      setGoalDatetime(datetime);
      setPercentTW(tw);
      setTargetWeightCardData({
        ...buildTargetWeightCardData(tw, datetime, targetWeightTooltip),
        secondaryText: `${weightData[0].value} lbs / ${getMedianGoalWeight(
          latestActiveWeightGoal,
        )}`,
      });
    } else setTargetWeightCardData(null);
  }, [
    heightData?.[0],
    weightData?.[0],
    medianBmiData?.medianBMI,
    weightGoalsResponse,
    latestActiveWeightGoal,
  ]);

  const ClinicalMeasurementCardDefinitions: MeasurementCardDefinition[] = [
    {
      dataToGraph: heightData,
      id: `${TOP_LEVEL_ID}__height`,
      mainObservation: heightData[0] as ObservationContent,
      profileType: ObservationCards.HEIGHT,
      title: 'Height',
    },
    {
      dataToGraph: weightData,
      id: `${TOP_LEVEL_ID}__weight`,
      mainObservation: weightData[0] as ObservationContent,
      profileType: ObservationCards.WEIGHT,
      title: 'Weight',
    },
    buildBmiCardData({
      bmi: rawBmi,
      datetime: bmiDatetime,
      percentMBmi,
    }),
    {
      dataToGraph: [],
      id: `${TOP_LEVEL_ID}__heart-rate`,
      mainObservation: heartRateData[0] as ObservationContent,
      profileType: ObservationCards.HEART_RATE,
      title: 'Heart rate',
    },
    {
      dataToGraph: [],
      id: `${TOP_LEVEL_ID}__blood-pressure`,
      mainObservation: bloodPressureData[0] as ObservationContent,
      profileType: ObservationCards.BLOOD_PRESSURE,
      title: 'Blood pressure',
    },
  ];

  if (
    weightGoalsResponse &&
    weightData?.[0] &&
    latestActiveWeightGoal?.goalType !== WeightGoalTypes.WEIGHT_MONITORING
  ) {
    const weightGoalIndex = ClinicalMeasurementCardDefinitions.findIndex(
      ({ profileType }) => profileType === ObservationCards.WEIGHT,
    );
    // Inserting %TW card right after Weight card, if there is a %TW
    if (weightGoalIndex !== -1 && percentTW !== 0) {
      ClinicalMeasurementCardDefinitions.splice(
        weightGoalIndex + 1,
        0,
        buildTargetWeightCardData(percentTW, goalDatetime, targetWeightTooltip),
      );
    }
  }

  return (
    <ChakraWrap spacing="16px">
      {ClinicalMeasurementCardDefinitions.map(
        ({
          dataToGraph,
          id,
          mainObservation,
          profileType,
          secondaryText,
          title,
          tooltipContent,
        }) => {
          return (
            <ChakraWrapItem key={id}>
              <ChakraBox height="126px" minWidth="235px" width="fit-content">
                <ClinicalMeasurementCard
                  graphData={dataToGraph
                    ?.map(({ value }) => ({ value }))
                    .reverse()}
                  id={id}
                  isLoading={isObservationsDataLoading}
                  mainObservation={mainObservation}
                  onClick={() => onClickCard(profileType)}
                  profileType={profileType}
                  secondaryText={secondaryText}
                  title={title}
                  tooltipContent={tooltipContent}
                />
              </ChakraBox>
            </ChakraWrapItem>
          );
        },
      )}
      <ChakraWrapItem key="single-patient-report-card">
        <ChakraBox height="126px" minWidth="235px" width="fit-content">
          <SinglePatientReportCard
            patientId={patient.userId}
            shouldFillHeight
          />
        </ChakraBox>
      </ChakraWrapItem>
    </ChakraWrap>
  );
};

export default ClinicalMeasurementOverview;
